import { startLoading, endLoading } from './app-dux';
import { fetchGql } from '../utils/fetch-gql';

/*******************************************************************************
 * Initial state
 */
const initialState = {
	projectList: [],
	selectedProjectBundle: {
		locale: '',
		keys: []
	},
	localeList: [],
	filteredProjectBundle: [],
	searchCriteria: '',
	selectedLocale: {
		code: '',
		label: ''
	}
};

/*******************************************************************************
 * Non-async action types
 */
export const SET_PROJECTS = 'SET_PROJECTS';
export const CLEAR_FILTERS = 'CLEAR_FILTERS';
export const SET_LOCALE_LIST = 'SET_LOCALE_LIST';
export const SET_PROJECT_KEYS = 'SET_PROJECT_KEYS';
export const UPDATE_PROJECT_KEYS = 'UPDATE_PROJECT_KEYS';
export const UPDATE_SELECTED_LOCALE = 'UPDATE_SELECTED_LOCALE';
export const SET_SELECTED_PROGRAM_BUNDLE = 'SET_SELECTED_PROGRAM_BUNDLE';
export const CLEAR_SELECTED_PROGRAM_BUNDLE = 'CLEAR_SELECTED_PROGRAM_BUNDLE';

/*******************************************************************************
 * Reducer
 */
export default function appReducer( state = initialState, action ) {

	switch ( action.type ) {
		case SET_PROJECTS:
			return {
				...state,
				projectList: action.projects.sort( ( a, b ) => a.name.localeCompare( b.name, 'en', { 'sensitivity': 'base' } ) )
			};
		case SET_LOCALE_LIST:
			return {
				...state,
				localeList: action.locales
			};
		case SET_PROJECT_KEYS:
			let selectedProjectBundle = action.project.bundles.find( project => project.locale === action.locale );
			if ( !selectedProjectBundle ) {
				selectedProjectBundle = {};
			}
			return {
				...state,
				selectedProjectBundle,
				filteredProjectBundle: selectedProjectBundle && selectedProjectBundle.keys
			};
		case UPDATE_PROJECT_KEYS:
			const updatedKeyList = state.selectedProjectBundle.keys.filter( item => {
				const isValid = Object.values( item ).some( a => a.toLowerCase().includes( action.searchString.toLowerCase() ) );
				return isValid ? item : undefined;
			} );

			return {
				...state,
				searchCriteria: action.searchString,
				filteredProjectBundle: updatedKeyList
			};
		case UPDATE_SELECTED_LOCALE:
			return {
				...state,
				selectedLocale: action.locale
			};
		case CLEAR_SELECTED_PROGRAM_BUNDLE:
			return {
				...state,
				filteredProjectBundle: [],
				selectedProjectBundle: initialState.selectedProjectBundle
			};
		case CLEAR_FILTERS:
			return {
				...state,
				searchCriteria: ''
			};
		default:
			return state;
	}
}

/*******************************************************************************
 * Action creators
 */
export const loadProjectList = () => async( dispatch, getState ) => {
	const gqlQuery = `query { 
                        projectList {
                            id
                            name
                        }
                      }`;

	dispatch( startLoading() );

	try {
		const { data, errors } = await fetchGql( gqlQuery );

		if ( errors ) {
			throw new Error( errors );
		}

		dispatch( setProjectList( data.projectList ) );
		return data;
	} catch ( error ) {
		throw new Error( error );
	} finally {
		dispatch( endLoading() );
	}
};

export const createProject = ( projectName ) => async( dispatch, getState ) => {
	const gqlQuery = `mutation { 
                        createProject( projectName: "${ projectName }" ) {
                            id
                            name
                        }
                      }`;

	dispatch( startLoading() );

	try {
		const { data, errors } = await fetchGql( gqlQuery );

		if ( errors ) {
			throw new Error( errors );
		}

		return data.createProject;
	} catch ( error ) {
		throw new Error( error );
	} finally {
		dispatch( endLoading() );
	}
};

export const loadProject = ( projectName, locale ) => async( dispatch ) => {
	const gqlQuery = `query { 
                        projectKeyList( projectName: "${ projectName }", locales: [ "${ locale }" ] ) {
                            bundles {
                                locale
                                keys {
                                    name
                                    goldCopy
                                    customCopy
                                }
                            }
                        }
                      }`;

	dispatch( startLoading() );

	try {
		const { data, errors } = await fetchGql( gqlQuery );

		if ( errors ) {
			throw new Error( errors );
		}

		dispatch( setProjectKeys( data.projectKeyList, locale ) );
		return data.projectKeyList;
	} catch ( error ) {
		throw new Error( error );
	} finally {
		dispatch( endLoading() );
	}
};

export const getLocales = () => async( dispatch ) => {
	const gqlQuery = `query {
	                    localeList {
                            code
                            label
                        }
                      }`;

	dispatch( startLoading() );

	try {
		const { data, errors } = await fetchGql( gqlQuery );

		if ( errors ) {
			throw new Error( errors );
		}

		dispatch( setLocaleList( data.localeList ) );
		return data.localeList;
	} catch ( error ) {
		throw new Error( error );
	} finally {
		dispatch( endLoading() );
	}
};

export const clearFilters = () => ( { type: CLEAR_FILTERS } );

export const setProjectList = projects => ( { type: SET_PROJECTS, projects } );

export const setProjectKeys = ( project, locale ) => ( { type: SET_PROJECT_KEYS, project, locale } );

export const setLocaleList = locales => ( { type: SET_LOCALE_LIST, locales } );

export const setSelectedProgramBundle = locale => ( { type: SET_SELECTED_PROGRAM_BUNDLE, locale } );

export const clearSelectedProgramBundle = () => ( { type: CLEAR_SELECTED_PROGRAM_BUNDLE } );

export const updateProjectKeys = searchString => ( { type: UPDATE_PROJECT_KEYS, searchString } );

export const updateSelectedLocale = locale => ( { type: UPDATE_SELECTED_LOCALE, locale } );